import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { approvalsGetApprovalOptions } from '@column/api';
import { Button, Icon, Toggle } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import {
  isApprovalCandidateStatus,
  isApprovalHold,
  submitApprovalHold,
} from '~/app/pages/Transfers/_utils/transferReview';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useBankAccount, useCounterparty, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useOptimisticTransferUpdate } from '~/lib/api';
import { useNavigate } from '~/lib/navigation';
import { TransferHeader, TransferStatus } from '~/lib/transfers';
import { RealtimeTransfer, convertTransferAmount } from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ReviewTransferRequest } from '~/typings/enum';

import { RealtimeReturnModal } from './_components/RealtimeReturnModal';
import { Timeline } from './_components/Timeline';
import { TransferReview } from './_components/TransferReview';
import { buildRealtimeEvents, isReturnedTransfer, isReturningTransfer } from './_utils/realtime';

interface Params {
  id: string;
}

const InfoForm = styled.form`
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

export const PageTransfersRealtime: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { isAuthorized } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const [accountNumber, setAccountNumber] = useState<any>({});
  const [isAccountNumberLoading, setIsAccountNumberLoading] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState(false);

  const { response: bankAccount, createRequest: fetchBankAccount } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: t,
    isLoading,
    createRequest: fetchTransfer,
  } = useTransfer<RealtimeTransfer>('realtime')({
    initialParams: { id },
    onSuccess: async (response) => {
      fetchAccountNumber(response.accountNumberId);

      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      if (response?.counterpartyId) {
        fetchCounterparty({ id: response.counterpartyId });
      }
    },

    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });
  useEffect(() => {
    fetchTransfer({ id });
  }, [id]);
  const transfer = useMemo(() => {
    if (!t) return;
    const initiatedAt = t.initiatedAt ?? t.completedAt ?? undefined;
    return { ...t, initiatedAt };
  }, [t]);

  const [isReviewLoading, setReviewLoading] = useState(false);
  const optimisticallyUpdateTransfer = useOptimisticTransferUpdate();
  const isApprovalCandidate = isApprovalCandidateStatus(transfer?.status);
  const { data: approvalState } = useQuery({
    ...approvalsGetApprovalOptions({ path: { object_id: id } }),
    enabled: isApprovalCandidate,
  });
  const isPendingApproval = isApprovalCandidate && approvalState?.status === 'pending';
  const isMarkedApprovalHold = isPendingApproval && isApprovalHold(transfer?.status);
  const reviewTransfer = useCallback(
    async (req: ReviewTransferRequest) => {
      setReviewLoading(true);
      try {
        const reviewedTransfer = await submitApprovalHold({ id, request: req, transferType: 'realtime' });
        optimisticallyUpdateTransfer({ id: reviewedTransfer.id, status: reviewedTransfer.status });
        await fetchTransfer({ id });
      } catch (err) {
        addDangerNotification({
          content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
        });
      } finally {
        setReviewLoading(false);
      }
    },
    [addDangerNotification, fetchTransfer, id, optimisticallyUpdateTransfer]
  );

  const { response: counterparty, createRequest: fetchCounterparty } = useCounterparty({
    onError: (error) => addDangerNotification({ content: error.message }),
  });

  const fetchAccountNumber = (accountNumberId?: string) => {
    if (!accountNumberId) {
      setIsAccountNumberLoading(false);
      return;
    }

    setIsAccountNumberLoading(true);
    BankAccountRepository.getAccountNumber({ id: accountNumberId })
      .then(setAccountNumber)
      .catch((error) => {
        addDangerNotification({ content: error.message });
      })
      .finally(() => setIsAccountNumberLoading(false));
  };

  const formMethods = useForm<RealtimeTransfer>();

  useEffect(() => {
    if (transfer) {
      formMethods.setValue('description', transfer.description || '-');
      formMethods.setValue('idempotencyKey', transfer.idempotencyKey || '-');
      formMethods.setValue('allowOverdraft', transfer.allowOverdraft || false);
    }
  }, [transfer]);

  const onReturn = useCallback(() => {
    setIsOpen(true);
  }, []);

  const isDataLoading = useMemo(() => {
    return isLoading || isAccountNumberLoading;
  }, [isLoading, isAccountNumberLoading]);

  // A re-held incoming transfer (its return was rejected by the network) carries a pair but is
  // live and awaiting a clear-or-return decision, not returned. hold is absent from the generated
  // status union while the feature ships dark, hence the widening.
  const status =
    transfer?.returnPairTransferId && transfer?.isIncoming && (transfer?.status as string) !== TransferStatus.HOLD
      ? TransferStatus.RETURNED
      : transfer?.status;

  return (
    <>
      <RealtimeReturnModal transferId={transfer?.id ?? ''} isOpen={isOpen} onClose={() => setIsOpen(false)} />

      <PageActions>
        {transfer && isReturnedTransfer(transfer) ? (
          <Button
            variant="muted"
            size="small"
            icon={<Icon.Return />}
            onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/realtime/${transfer.returnPairTransferId}`)}
          >
            Returned
          </Button>
        ) : transfer && isReturningTransfer(transfer) ? (
          <Button
            variant="muted"
            size="small"
            icon={<Icon.Return />}
            onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/realtime/${transfer.returnPairTransferId}`)}
          >
            Original Transfer
          </Button>
        ) : (
          transfer?.completedAt &&
          transfer?.isIncoming && (
            <Button variant="danger" size="small" onClick={onReturn}>
              Return
            </Button>
          )
        )}
      </PageActions>

      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferHeader
            amount={transfer.amount!}
            createdAt={transfer.initiatedAt ?? undefined}
            isAdding={transfer ? convertTransferAmount('realtime', transfer.isIncoming ?? false) > 0 : undefined}
            direction={{
              type: transfer?.isIncoming ? 'incoming' : 'outgoing',
            }}
            status={isMarkedApprovalHold ? 'pre_review' : status}
            transferId={transfer.id!}
            transferType="realtime"
          />
        )}
        <TransferReview
          amount={transfer?.amount}
          bankAccountName={bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account'}
          counterpartyName={counterparty?.name || counterparty?.description || 'Unnamed Counterparty'}
          createdByName={(transfer as { createdBy?: { name?: string } } | undefined)?.createdBy?.name}
          description={transfer?.description}
          isDisabled={
            !isAuthorized({ permission: 'approveTransfers', permissionLevel: 'write', bankAccount }) ||
            !approvalState?.can_review
          }
          isLoading={isReviewLoading}
          isOpen={!!transfer && isPendingApproval}
          onSubmit={reviewTransfer}
        />
        {bankAccount && accountNumber && counterparty && transfer && !isLoadingEntity && (
          <Timeline
            entries={buildRealtimeEvents({
              transfer,
              account: {
                accountNumber,
                bankAccount,
                entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
              },
              counterparty,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<RealtimeTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value)} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Indempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={String(value) || ''} isMonospaced />;
                      },
                    },
                    {
                      id: 'allowOverdraft',
                      label: 'Allow Overdraft',
                      children: ({ value, onChange }) => {
                        const isChecked = typeof value === 'boolean' ? value : false;
                        return <Toggle isChecked={isChecked} onCheckedChange={onChange} isDisabled />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Transfer Details"
              formattedData={transfer as Record<string, unknown>}
              rawData={transfer as Record<string, unknown>}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
